/**
 * Rules for how many players a team is allowed to have active.
 *
 * This used to be one comparison written inline on the Agregar Jugador button. A
 * dependency-update commit dropped it while rewriting the surrounding markup, and
 * from then on nothing in the client checked the active count: a captain could
 * keep adding players, each one active by default, until they ran out of allowed
 * changes. That is how production teams ended up with 16 to 20 active players
 * against a limit of 15.
 *
 * It lives here, apart from the component, for two reasons.
 *
 * The limit arrives as a parameter instead of being read from `src/config.json`.
 * That file is generated per tournament by `scripts/config.js` and committed, so
 * whatever it says depends on whoever last ran a publish script. Most tournaments
 * play with 15; f5 plays with 12. A rule this important should not quietly change
 * meaning because someone ran `yarn f5`.
 *
 * `isAdmin` is a boolean rather than a profile object. The caller passes the
 * result of `esAdminOsuperAdmin()`, so "is this an admin" keeps one definition in
 * the codebase and there is nothing here to drift out of sync with it.
 *
 * Identifiers in this module are English, while the domain vocabulary of the
 * surrounding codebase is Spanish (`activo`, `jugadores`, `cambios`, `capitan`).
 * References to existing fields, config keys and UI strings therefore stay in
 * Spanish, because those are the real names of things that already exist.
 */

/* eslint-disable import/prefer-default-export */

/**
 * Should the Agregar Jugador button refuse to add another player?
 *
 * This only ever adds a reason to disable. It never enables anything, so wiring
 * it in as one extra condition cannot make a button clickable that is not
 * clickable today.
 *
 * It refuses when it cannot work out the answer. `getActivos()` returns an empty
 * object while the roster is unknown, and `undefined >= 15` is false — so a
 * missing count would quietly read as "there is room", in exactly the loading and
 * empty states where nobody would notice. Treating an unknown count as full is
 * the safe direction.
 *
 * `Number.isFinite` matters here and the global `isFinite` would be a bug. The
 * global one converts its argument first: `isFinite(null)` and `isFinite('')` are
 * both true, so both would count as valid rosters with room to spare.
 * `Number.isFinite` does no conversion, which leaves a real zero — an empty team,
 * genuinely not full — behaving normally while everything unknown refuses.
 *
 * @param {number|undefined|null} activeCount how many players are already active
 * @param {number} limit        the tournament's `LIMITE_JUGADORES`, read by the caller
 * @param {boolean} isAdmin     whether this is an admin, who is allowed past it
 * @returns {boolean} true when the button should be disabled
 */
export const isBlockedByLimit = ({ activeCount, limit, isAdmin }) => {
  // Admins have always been able to go past the limit, and that is intentional:
  // they are the ones who fix a roster when something went wrong. This is a
  // decision about who is asking, so it never looks at the numbers.
  if (isAdmin) return false;

  // A limit we cannot read is not a limit we can enforce, so refuse.
  if (!Number.isFinite(limit) || limit <= 0) return true;

  // Same for a count we cannot trust.
  if (!Number.isFinite(activeCount)) return true;

  return activeCount >= limit;
};

/**
 * How many players in `items` count as active, by the same truthiness the
 * player list itself uses: anything truthy is active, everything else is not.
 * No captain exclusion, no special case — this must agree with the list's own
 * count or the save-time check and the render-time button would disagree.
 *
 * @param {Array<Object>} items the roster, shaped like a `traerJugadores()` result
 * @returns {number}
 */
export const countActive = (items) =>
  items.filter((item) => item.activo).length;

/**
 * What the roster would look like after the selected players take the
 * requested `activo` state. Pure and synchronous: a projection, never a write.
 * There is deliberately no write flag in the signature, so a persistence call
 * from render is structurally impossible.
 *
 * The captain is always active, matching the standing behaviour that a
 * Desactivar selection can never remove the captain from the active list.
 * Unselected players come back unchanged, exactly as they were.
 *
 * @param {Array<Object>} items      the roster
 * @param {(identification: string) => boolean} isSelected whether a player is selected
 * @param {boolean} activar  the `activo` state selected players should end up with
 * @returns {Array<Object>} the projected roster, one entry per input item
 */
export const projectActive = (items, isSelected, activar) =>
  items.map((item) => {
    if (!isSelected(item.identification)) return item;
    return { ...item, activo: item.capitan || activar };
  });
